import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { z } from 'zod';
import { request, useQuery } from '@applyboard/ui-utils';
import { toIsoCountry } from '../common/constants';
import {
  StudentDetails,
  StudentDetailsGender,
  StudentDetailsMaritalStatus,
} from '../components/LandingPage/StudentDetails';

const GET_STUDENT_URL = '/api/v0.1/students';

export function useGetStudentDetails(
  legacyStudentId: string,
  options: { enabled?: boolean } = { enabled: true },
) {
  const { isFetching, error, data, isLoading } = useQuery(
    ['studentDetails', legacyStudentId],
    async (): Promise<StudentDetails> => {
      const response = await request(`${GET_STUDENT_URL}/${legacyStudentId}`, {
        schema: studentDetailsResponseBody,
      });

      const studentProfile = response?.profile;
      const studentPersonalInformation = studentProfile?.personal_information;
      const residentialAddress = studentProfile?.residential_address?.address;

      let formattedPhoneNumber = undefined;
      if (studentPersonalInformation?.phone) {
        const parsedStudentNumber = parsePhoneNumberFromString(studentPersonalInformation?.phone);
        formattedPhoneNumber =
          parsedStudentNumber?.countryCallingCode && parsedStudentNumber?.nationalNumber
            ? `+${parsedStudentNumber.countryCallingCode}${parsedStudentNumber.nationalNumber}`
            : undefined;
      }

      return {
        student: {
          nationality: toIsoCountry(
            studentPersonalInformation?.country_of_citizenship ?? undefined,
          ),
          gender: toGender(studentPersonalInformation?.gender ?? undefined),
          maritalStatus: toMaritalStatus(studentPersonalInformation?.marital_status ?? undefined),
          givenName: studentPersonalInformation?.first_name ?? undefined,
          surname: studentPersonalInformation?.last_name ?? undefined,
          dateOfBirth: parseDate(studentPersonalInformation?.birthdate),
        },
        contact: {
          emailAddress: studentProfile?.email
            ? studentProfile?.email
            : studentPersonalInformation?.primary_email ?? undefined,
          phoneNumber: formattedPhoneNumber,
        },
        residentialAddress: {
          numberAndStreetName: residentialAddress?.street ?? undefined,
          city: residentialAddress?.city ?? undefined,
          stateOrProvince: residentialAddress?.province ?? undefined,
          country: toIsoCountry(residentialAddress?.country ?? undefined),
          postalCode: residentialAddress?.postal ?? undefined,
        },
        passport: {
          passportNumber: studentPersonalInformation?.passport_number ?? undefined,
          expiryDate: parseDate(studentPersonalInformation?.passport_expiry_date),
        },
      };
    },
    options,
  );

  return {
    isFetchingStudentDetails: isFetching,
    isLoadingStudentDetails: isLoading,
    studentDetailsError: error,
    studentDetails: data,
  };
}

const parseDate = (input: string | undefined | null) => {
  if (!input) {
    return undefined;
  }
  // from packages/ui-components/src/components/KeyboardDatePicker/index.tsx
  const date = new Date(`${input}T00:00:00Z`);
  // We need to set an offset for the user's local timezone
  // so the date picker properly interprets the date
  date.setMinutes(date.getMinutes() + date.getTimezoneOffset());

  if (isNaN(date.getTime())) {
    return undefined;
  }
  return date;
};

export type StudentDetailsResponseBody = z.infer<typeof studentDetailsResponseBody>;
const studentDetailsResponseBody = z.object({
  profile: z
    .object({
      email: z.string().trim().nullish(),
      personal_information: z
        .object({
          first_name: z.string().trim().nullish(),
          last_name: z.string().trim().nullish(),
          birthdate: z.string().trim().nullish(),
          primary_email: z.string().trim().nullish(),
          phone: z.string().trim().nullish(),
          country_of_citizenship: z.string().trim().nullish(),
          gender: z.string().trim().nullish(),
          marital_status: z.string().trim().nullish(),
          passport_number: z.string().trim().nullish(),
          passport_expiry_date: z.string().trim().nullish(),
        })
        .nullish(),
      residential_address: z
        .object({
          address: z
            .object({
              street: z.string().trim().nullish(),
              city: z.string().trim().nullish(),
              province: z.string().trim().nullish(),
              postal: z.string().trim().nullish(),
              country: z.string().trim().nullish(),
            })
            .nullish(),
        })
        .nullish(),
    })
    .nullish(),
});

function toGender(value: string = ''): StudentDetailsGender | undefined {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (<any>Object).values(StudentDetailsGender).includes(value)
    ? (value as StudentDetailsGender)
    : undefined;
}

function toMaritalStatus(value: string = ''): StudentDetailsMaritalStatus | undefined {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (<any>Object).values(StudentDetailsMaritalStatus).includes(value)
    ? (value as StudentDetailsMaritalStatus)
    : undefined;
}
